/**
 * @fileoverview Section "Colors" de la page `/design-system`.
 *
 * Rend la palette Quipux (navy, orange, blue) puis les aliases sémantiques
 * UI, les statuts feedback et les statuts métier GNPC. Chaque swatch lit
 * son hex directement dans le DOM (voir `ColorSwatch`) : ce catalogue
 * reste synchrone avec `packages/ui/src/styles/globals.css` sans avoir
 * à dupliquer les valeurs.
 */

import { ColorSwatch } from '../components/ColorSwatch.tsx';

/**
 * Une palette (11 nuances 50 → 950) rendue en grille horizontale.
 */
interface PaletteRowProps {
  title: string;
  description: string;
  base: string;
  /** Si toutes les nuances sont considérées "sombres" à partir de… */
  darkFrom?: number;
}

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

function PaletteRow({ title, description, base, darkFrom = 500 }: PaletteRowProps): JSX.Element {
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-quipux-navy-950">{title}</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6 lg:grid-cols-11">
        {SHADES.map((shade) => (
          <ColorSwatch
            key={shade}
            name={`${base}-${shade}`}
            className={`bg-${base}-${shade}`}
            dark={shade >= darkFrom}
          />
        ))}
      </div>
    </div>
  );
}

interface AliasRowProps {
  title: string;
  description: string;
  aliases: ReadonlyArray<{ name: string; className: string; dark?: boolean }>;
}

function AliasRow({ title, description, aliases }: AliasRowProps): JSX.Element {
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-quipux-navy-950">{title}</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {aliases.map((a) => (
          <ColorSwatch key={a.name} name={a.name} className={a.className} dark={a.dark} />
        ))}
      </div>
    </div>
  );
}

export function DsSectionColors(): JSX.Element {
  return (
    <div className="space-y-8">
      <PaletteRow
        title="Quipux Navy"
        description="Identité principale — wordmark, textes, sidebar, fond sombre. Base #001540 (navy-950)."
        base="quipux-navy"
        darkFrom={500}
      />
      <PaletteRow
        title="Quipux Orange"
        description="Accent primaire — CTA, focus ring, statuts d'action. Base #ff4800 (orange-500)."
        base="quipux-orange"
        darkFrom={600}
      />
      <PaletteRow
        title="Quipux Blue"
        description="Accent secondaire — liens, info, éléments décoratifs. Base #3B9BD9 (blue-500)."
        base="quipux-blue"
        darkFrom={500}
      />

      <AliasRow
        title="UI sémantiques"
        description="À utiliser dans les composants applicatifs — jamais les tokens brand directs."
        aliases={[
          { name: 'background', className: 'bg-background' },
          { name: 'foreground', className: 'bg-foreground', dark: true },
          { name: 'primary', className: 'bg-primary', dark: true },
          { name: 'secondary', className: 'bg-secondary', dark: true },
          { name: 'accent', className: 'bg-accent', dark: true },
          { name: 'muted', className: 'bg-muted' },
          { name: 'border', className: 'bg-border' },
          { name: 'input', className: 'bg-input' },
          { name: 'ring', className: 'bg-ring', dark: true },
          { name: 'card', className: 'bg-card' },
          { name: 'popover', className: 'bg-popover' },
          { name: 'destructive', className: 'bg-destructive', dark: true },
        ]}
      />

      <AliasRow
        title="Feedback"
        description="Statuts d'interaction — utilisés par Alert, Toast, InlineError, Banner."
        aliases={[
          { name: 'success', className: 'bg-success', dark: true },
          { name: 'warning', className: 'bg-warning' },
          { name: 'danger', className: 'bg-danger', dark: true },
          { name: 'info', className: 'bg-info', dark: true },
        ]}
      />

      <AliasRow
        title="Statuts métier — Paiement"
        description="Utilisés par StatusBadge (spécialisations PaymentStatus / SgiStatus / RefundStatus)."
        aliases={[
          { name: 'status-initiated', className: 'bg-status-initiated', dark: true },
          { name: 'status-pending', className: 'bg-status-pending' },
          { name: 'status-paid', className: 'bg-status-paid', dark: true },
          { name: 'status-failed', className: 'bg-status-failed', dark: true },
          { name: 'status-expired', className: 'bg-status-expired', dark: true },
          { name: 'status-recorded', className: 'bg-status-recorded', dark: true },
          { name: 'status-partial', className: 'bg-status-partial' },
          { name: 'status-not-applicable', className: 'bg-status-not-applicable' },
        ]}
      />

      <AliasRow
        title="Statuts intégrations"
        description="Connecteurs — cadrans UP/DOWN/DEGRADED."
        aliases={[
          { name: 'integration-up', className: 'bg-integration-up', dark: true },
          { name: 'integration-degraded', className: 'bg-integration-degraded' },
          { name: 'integration-down', className: 'bg-integration-down', dark: true },
        ]}
      />
    </div>
  );
}
